// Typed API client for the operator portal. Pure + dependency-injected (baseUrl, token getter,
// fetch) so it is unit-testable in node without a DOM. The portal is a THIN consumer of the API —
// no business logic lives here (PRD §9 / M9).

export interface ApiUser {
  id: string;
  email: string;
  name: string;
  role: string;
  tenant_id: string;
}

export interface LoginResponse {
  token: string;
  user: ApiUser;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

export interface ApiClientOptions {
  baseUrl: string;
  getToken: () => string | null;
  fetchFn?: typeof fetch;
}

export interface ProviderFlags {
  password: boolean;
  otp: boolean;
  google: boolean;
  cloudflare: boolean;
}

export interface ApiClient {
  login(email: string, password: string): Promise<LoginResponse>;
  me(): Promise<ApiUser>;
  logout(): Promise<void>;
  providers(): Promise<ProviderFlags>;
  otpRequest(email: string): Promise<{ sent: boolean }>;
  otpVerify(email: string, code: string): Promise<LoginResponse>;
  oauthStartUrl(provider: string): string;
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  del(path: string): Promise<void>;
}

export function createApiClient(opts: ApiClientOptions): ApiClient {
  const fetchFn = opts.fetchFn ?? fetch;

  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = {};
    const token = opts.getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
    if (body !== undefined) headers['content-type'] = 'application/json';
    // /v1 writes require an Idempotency-Key (invariant #9). One per request is fine for the portal.
    if (method !== 'GET') headers['Idempotency-Key'] = crypto.randomUUID();

    const res = await fetchFn(`${opts.baseUrl}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });

    if (res.status === 204) return undefined as T;
    const data: unknown = await res.json().catch(() => null);
    if (!res.ok) {
      const d = (data ?? {}) as { code?: string; title?: string };
      throw new ApiError(res.status, d.code ?? 'error', d.title ?? `HTTP ${res.status}`);
    }
    return data as T;
  }

  return {
    login: (email, password) => request<LoginResponse>('POST', '/auth/login', { email, password }),
    me: () => request<ApiUser>('GET', '/auth/me'),
    logout: () => request<void>('POST', '/auth/logout', {}),
    providers: () => request<ProviderFlags>('GET', '/auth/providers'),
    otpRequest: (email) => request<{ sent: boolean }>('POST', '/auth/otp/request', { email }),
    otpVerify: (email, code) => request<LoginResponse>('POST', '/auth/otp/verify', { email, code }),
    oauthStartUrl: (provider) => `${opts.baseUrl}/auth/oauth/${provider}/start`,
    get: <T>(path: string) => request<T>('GET', path),
    post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
    patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
    del: (path: string) => request<void>('DELETE', path),
  };
}
